import React, { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';

/**
 * NumberOfResourcesByCollege
 * - Horizontal bar chart: college names (y) vs total_count (x).
 * - records: [{ col_name, col_display, total_count, ... }]
 * - Sorted by total_count descending.
 */
export default function NumberOfResourcesByCollege({ records = [] }) {
  const ref = useRef(null);

  const items = useMemo(() => {
    if (!Array.isArray(records) || !records.length) return [];
    return [...records]
      .filter((r) => r != null && (r.col_name != null || r.col_display != null))
      .map((r) => ({
        label: String(r.col_display || r.col_name || '').trim() || String(r.col_name || ''),
        value: Number(r.total_count) || 0,
      }));
      // .sort((a, b) => b.value - a.value);
  }, [records]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !items.length) {
      if (el) el.innerHTML = '';
      return;
    }

    const draw = () => {
      el.innerHTML = '';
      const width = el.clientWidth || 800;
      // CollegeRateByDBChart와 동일: 막대 높이·간격으로 옆 차트와 같은 크기
      const barHeight = 10;
      const barGap = 4;
      const n = items.length;
      const labelColWidth =
        n <= 8 ? width * 0.2 : n <= 15 ? width * 0.25 : width * 0.18;
      const rightMargin = 36;
      const top = 8;
      const bottom = 30;
      const margin = { top, right: rightMargin, bottom, left: labelColWidth };

      const innerH = n * barHeight + (n - 1) * barGap;
      const height = innerH + top + bottom;
      const innerW = Math.max(0, width - margin.left - margin.right);

      const maxVal = Math.max(1, d3.max(items, (d) => d.value) || 1);
      const x = d3.scaleLinear().domain([0, maxVal]).range([0, innerW]).nice();
      const yPos = (i) => top + i * (barHeight + barGap);

      const svg = d3
        .select(el)
        .append('svg')
        .attr('width', '100%')
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`);

      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      // Grid lines (vertical)
      const xTicks = x.ticks(5);
      g.append('g')
        .selectAll('line')
        .data(xTicks)
        .enter()
        .append('line')
        .attr('x1', (d) => x(d))
        .attr('x2', (d) => x(d))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#E4E8F1');

      // Tooltip (CollegeRateByDBChart 스타일)
      d3.selectAll('.number-of-resources-tooltip').remove();
      const tooltip = d3.select('body')
        .append('div')
        .attr('class', 'number-of-resources-tooltip')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.85)')
        .style('color', 'white')
        .style('padding', '8px 12px')
        .style('border-radius', '6px')
        .style('font-size', '13px')
        .style('font-weight', '600')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 10000)
        .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.3)');

      // Bars
      g.selectAll('rect')
        .data(items)
        .enter()
        .append('rect')
        .attr('x', 0)
        .attr('y', (_, i) => yPos(i) - top)
        .attr('height', barHeight)
        .attr('width', (d) => x(d.value))
        .attr('fill', '#6EA2FF')
        .attr('rx', 0)
        .attr('ry', 0)
        .style('cursor', 'pointer')
        .on('mouseenter', function(event, d) {
          tooltip
            .html(`<div>${d.label}</div><div style="margin-top:4px;font-size:11px;opacity:0.9;">Resources: ${Number(d.value).toLocaleString()}</div>`)
            .style('opacity', 1);
        })
        .on('mousemove', function(event) {
          tooltip
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseleave', function() {
          tooltip.style('opacity', 0);
        });

      // X-axis (values)
      g.append('g')
        .attr('transform', `translate(0,${innerH})`)
        .call(d3.axisBottom(x).ticks(5))
        .call((gx) => {
          gx.selectAll('.domain').attr('stroke', '#E4E8F1');
          gx.selectAll('text').attr('fill', '#6C7687').style('font-size', 10);
          gx.selectAll('line').remove();
        });

      // Y-axis labels (college names) — CollegeRateByDBChart와 동일: 오른쪽 정렬, 회색, 11px
      const labelsGroup = svg
        .append('g')
        .attr('transform', `translate(${labelColWidth - 8},${margin.top})`);

      labelsGroup
        .selectAll('text')
        .data(items)
        .enter()
        .append('text')
        .attr('x', 0)
        .attr('y', (_, i) => i * (barHeight + barGap) + barHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('fill', '#6C7687')
        .style('font-size', 11)
        .text((d) => d.label);
    };

    draw();
    let ro;
    try {
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(() => draw());
        ro.observe(el);
      } else {
        window.addEventListener('resize', draw);
      }
    } catch {
      window.addEventListener('resize', draw);
    }
    return () => {
      try {
        if (ro) ro.disconnect();
        else window.removeEventListener('resize', draw);
        d3.selectAll('.number-of-resources-tooltip').remove();
      } catch {}
    };
  }, [items]);

  if (!items.length) {
    return (
      <div className="flex items-center justify-center min-h-[200px] text-[#8D9AAF] text-sm">
        데이터가 없습니다.
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      <div ref={ref} className="w-full" />
    </div>
  );
}
